(() => {
  const loader = document.getElementById('sm-page-loader');
  if (!loader) return;

  const root = document.documentElement;
  const HIDDEN_CLASS = 'sm-page-loader--hidden';
  const TRANSITIONING_CLASS = 'sm-page-transitioning';
  const COLOR_SWITCH_KEY = 'sm:pdp-colour-switch';
  const minVisibleMs = 240;
  const startedAt = performance.now();
  let navigating = false;
  let colourNavigating = false;
  let colourRequest = null;

  let arrivingFromColourSwitch = false;
  try {
    arrivingFromColourSwitch = sessionStorage.getItem(COLOR_SWITCH_KEY) === '1';
    if (arrivingFromColourSwitch) sessionStorage.removeItem(COLOR_SWITCH_KEY);
  } catch (_) {}

  const hideLoaderImmediately = () => {
    loader.classList.add(HIDDEN_CLASS);
    root.classList.remove(TRANSITIONING_CLASS);
    loader.setAttribute('aria-hidden', 'true');
    root.classList.remove('sm-skip-page-loader');
  };

  if (arrivingFromColourSwitch) hideLoaderImmediately();

  const hideLoader = () => {
    if (arrivingFromColourSwitch) return;
    const elapsed = performance.now() - startedAt;
    const delay = Math.max(0, minVisibleMs - elapsed);

    window.setTimeout(() => {
      loader.classList.add(HIDDEN_CLASS);
      root.classList.remove(TRANSITIONING_CLASS);
      loader.setAttribute('aria-hidden', 'true');
    }, delay);
  };

  const showLoader = () => {
    loader.classList.remove(HIDDEN_CLASS);
    loader.removeAttribute('aria-hidden');
    root.classList.add(TRANSITIONING_CLASS);
  };

  const pageIsReady = async () => {
    if (arrivingFromColourSwitch) return;
    try {
      if (document.fonts && document.fonts.ready) {
        await Promise.race([
          document.fonts.ready,
          new Promise((resolve) => window.setTimeout(resolve, 700))
        ]);
      }
    } catch (_) {}
    requestAnimationFrame(() => requestAnimationFrame(hideLoader));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pageIsReady, { once: true });
  } else {
    pageIsReady();
  }

  window.setTimeout(hideLoader, 2800);

  const prefetched = new Map();
  const getColourLink = (target) => target.closest?.('.sm-related-colours--pdp .sm-related-colour-swatch[href], [data-sm-pdp-colour-link][href]');

  const fetchColourDocument = (url) => {
    if (prefetched.has(url)) return prefetched.get(url);

    const request = fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then((response) => {
      if (!response.ok) throw new Error(`PDP colour request failed: ${response.status}`);
      return response.text();
    }).finally(() => {
      // Keep successful responses cached during this PDP visit; remove failed ones.
    });

    request.catch(() => prefetched.delete(url));
    prefetched.set(url, request);
    return request;
  };

  const prefetchColourProduct = (link) => {
    if (!link?.href) return;
    fetchColourDocument(link.href).catch(() => {});
  };

  const executeInlineScripts = (scope) => {
    scope.querySelectorAll('script:not([src])').forEach((oldScript) => {
      const type = (oldScript.getAttribute('type') || '').trim();
      if (type && type !== 'text/javascript' && type !== 'application/javascript' && type !== 'module') return;

      const script = document.createElement('script');
      for (const attr of oldScript.attributes) script.setAttribute(attr.name, attr.value);
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  };

  const syncHeadForProduct = (nextDoc) => {
    document.title = nextDoc.title;

    const selectors = [
      'link[rel="canonical"]',
      'meta[property="og:title"]',
      'meta[property="og:url"]',
      'meta[property="og:image"]',
      'meta[property="og:image:secure_url"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:image"]'
    ];

    selectors.forEach((selector) => {
      const current = document.head.querySelector(selector);
      const incoming = nextDoc.head.querySelector(selector);
      if (current && incoming) {
        for (const attr of [...current.attributes]) current.removeAttribute(attr.name);
        for (const attr of incoming.attributes) current.setAttribute(attr.name, attr.value);
      }
    });
  };

  const findPdpSection = (doc) => {
    const swatches = doc.querySelector('.sm-related-colours--pdp');
    return swatches?.closest('.shopify-section') || null;
  };

  const swapPdpColour = async (url, { push = true } = {}) => {
    if (colourRequest) return;

    const currentSection = findPdpSection(document);
    if (!currentSection) {
      try { sessionStorage.setItem(COLOR_SWITCH_KEY, '1'); } catch (_) {}
      window.location.href = url;
      return;
    }

    colourNavigating = true;
    root.classList.add('sm-pdp-colour-updating');
    currentSection.setAttribute('aria-busy', 'true');

    const request = fetchColourDocument(url);
    colourRequest = request;

    try {
      const html = await request;
      const nextDoc = new DOMParser().parseFromString(html, 'text/html');
      const nextSection = findPdpSection(nextDoc);

      if (!nextSection) throw new Error('Destination PDP section not found');

      const importedSection = document.importNode(nextSection, true);
      const currentTop = window.scrollY;

      const performSwap = () => {
        currentSection.replaceWith(importedSection);
        syncHeadForProduct(nextDoc);
        if (push) {
          try {
            if (!history.state?.smPdpAjax) {
              history.replaceState({ ...(history.state || {}), smPdpAjax: true }, '', window.location.href);
            }
            history.pushState({ smPdpAjax: true }, '', url);
          } catch (_) {}
        }
        executeInlineScripts(importedSection);
        window.scrollTo({ top: currentTop, left: 0, behavior: 'instant' });
      };

      if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        await document.startViewTransition(performSwap).finished;
      } else {
        performSwap();
      }

      // Let theme/app observers see a clear lifecycle event after the new custom
      // elements have connected to the document.
      document.dispatchEvent(new CustomEvent('sm:pdp-colour-changed', { detail: { url } }));
    } catch (error) {
      console.warn('[Salt Murphy] Smooth PDP colour switch fell back to normal navigation.', error);
      try { sessionStorage.setItem(COLOR_SWITCH_KEY, '1'); } catch (_) {}
      window.location.href = url;
      return;
    } finally {
      colourRequest = null;
      colourNavigating = false;
      root.classList.remove('sm-pdp-colour-updating');
    }
  };

  document.addEventListener('pointerover', (event) => {
    const link = getColourLink(event.target);
    if (link) prefetchColourProduct(link);
  }, { passive: true });

  document.addEventListener('touchstart', (event) => {
    const link = getColourLink(event.target);
    if (link) prefetchColourProduct(link);
  }, { passive: true });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    if (!link) return;
    if (link.hasAttribute('download')) return;
    if (link.target && link.target.toLowerCase() !== '_self') return;

    // Collection/product-card colour swatches are handled entirely in-place by
    // custom.js. They must never trigger the full-page branded loader, because
    // there is no document navigation to wait for.
    if (link.matches('[data-sm-related-colour]') || link.closest('.sm-related-colours--card')) return;

    const isPdpColourSwitch = !!getColourLink(link);
    if (isPdpColourSwitch) {
      event.preventDefault();
      event.stopPropagation();
      swapPdpColour(link.href);
      return;
    }

    if (link.hasAttribute('data-no-page-loader')) return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (_) {
      return;
    }

    if (url.origin !== window.location.origin) return;
    if (!/^https?:$/.test(url.protocol)) return;

    if (
      url.pathname === window.location.pathname &&
      url.search === window.location.search &&
      url.hash
    ) return;

    if (
      link.matches('[href^="#"], [aria-controls], [data-no-instant]') ||
      link.closest('dialog, [role="dialog"], cart-drawer, quick-buy-modal')
    ) return;

    queueMicrotask(() => {
      if (event.defaultPrevented || navigating) return;
      navigating = true;
      showLoader();
    });
  }, true);

  window.addEventListener('popstate', (event) => {
    if (event.state?.smPdpAjax && document.querySelector('.sm-related-colours--pdp')) {
      swapPdpColour(window.location.href, { push: false });
    }
  });

  window.addEventListener('pageshow', (event) => {
    navigating = false;
    colourNavigating = false;
    if (event.persisted || arrivingFromColourSwitch) hideLoaderImmediately();
  });

  window.addEventListener('pagehide', () => {
    if (navigating && !colourNavigating) showLoader();
  });
})();
